import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import type { Pagination } from '../types/api';
import type {
  GamificationConfig,
  GamificationConfigResponse,
  Milestone,
  MilestonesResponse,
  MilestoneResponse,
  MilestoneCreate,
  MilestoneUpdate,
  Prize,
  PrizeCreate,
  PrizeUpdate,
  PrizesListResponse,
  PrizeResponse,
  PrizesQueryParams,
  PrizeClaim,
  PrizeClaimCreate,
  PrizeClaimAction,
  ClaimsListResponse,
  ClaimResponse,
  ClaimsQueryParams,
  PointsBalance,
  PointsBalanceResponse,
  LedgerEntry,
  LedgerListResponse,
  LedgerQueryParams,
  TenantLedgerQueryParams,
  AdminAdjust,
  LeaderboardRanking,
  LeaderboardQueryParams,
  AdminLeaderboardResponse,
  ResetSchedule,
  ResetScheduleResponse,
  ResetScheduleUpdate,
  GamificationSeason,
  SeasonsResponse,
} from '../types/gamification';

// ═══════════════════════════════════════════════════════════════════════════
// GAMIFICATION CONFIG
// ═══════════════════════════════════════════════════════════════════════════

export const fetchGamificationConfig = async (courseId: string): Promise<GamificationConfig> => {
  const response = await api.get<GamificationConfigResponse>(
    `/courses/${courseId}/gamification`
  );
  return response.data.data;
};

export const useGamificationConfig = (
  courseId: string | undefined
): UseQueryResult<GamificationConfig, Error> => {
  return useQuery({
    queryKey: ['gamification', courseId],
    queryFn: () => fetchGamificationConfig(courseId!),
    enabled: !!courseId,
    staleTime: 2 * 60 * 1000,
  });
};

export const enableGamification = async (courseId: string): Promise<GamificationConfig> => {
  const response = await api.post<GamificationConfigResponse>(
    `/courses/${courseId}/gamification/enable`
  );
  return response.data.data;
};

export const useEnableGamification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (courseId: string) => enableGamification(courseId),
    onSuccess: (_data, courseId) => {
      queryClient.invalidateQueries({ queryKey: ['gamification', courseId] });
    },
  });
};

export const disableGamification = async (courseId: string): Promise<void> => {
  await api.post(`/courses/${courseId}/gamification/disable`);
};

export const useDisableGamification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (courseId: string) => disableGamification(courseId),
    onSuccess: (_data, courseId) => {
      queryClient.invalidateQueries({ queryKey: ['gamification', courseId] });
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// MILESTONES
// ═══════════════════════════════════════════════════════════════════════════

export const fetchMilestones = async (courseId: string): Promise<Milestone[]> => {
  const response = await api.get<MilestonesResponse>(
    `/courses/${courseId}/gamification/milestones`
  );
  return response.data.data;
};

export const useMilestones = (
  courseId: string | undefined,
  options?: { enabled?: boolean }
): UseQueryResult<Milestone[], Error> => {
  return useQuery({
    queryKey: ['milestones', courseId],
    queryFn: () => fetchMilestones(courseId!),
    enabled: !!courseId && (options?.enabled !== false),
    staleTime: 2 * 60 * 1000,
  });
};

export const createMilestone = async (
  courseId: string,
  data: MilestoneCreate
): Promise<Milestone> => {
  const response = await api.post<MilestoneResponse>(
    `/courses/${courseId}/gamification/milestones`,
    data
  );
  return response.data.data;
};

export const useCreateMilestone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: MilestoneCreate }) =>
      createMilestone(courseId, data),
    onSuccess: (_data, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', courseId] });
      queryClient.invalidateQueries({ queryKey: ['gamification', courseId] });
    },
  });
};

export const updateMilestone = async (
  courseId: string,
  milestoneId: string,
  data: MilestoneUpdate
): Promise<Milestone> => {
  const response = await api.put<MilestoneResponse>(
    `/courses/${courseId}/gamification/milestones/${milestoneId}`,
    data
  );
  return response.data.data;
};

export const useUpdateMilestone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseId,
      milestoneId,
      data,
    }: {
      courseId: string;
      milestoneId: string;
      data: MilestoneUpdate;
    }) => updateMilestone(courseId, milestoneId, data),
    onSuccess: (_data, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', courseId] });
      queryClient.invalidateQueries({ queryKey: ['gamification', courseId] });
    },
  });
};

export const deleteMilestone = async (courseId: string, milestoneId: string): Promise<void> => {
  await api.delete(`/courses/${courseId}/gamification/milestones/${milestoneId}`);
};

export const useDeleteMilestone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, milestoneId }: { courseId: string; milestoneId: string }) =>
      deleteMilestone(courseId, milestoneId),
    onSuccess: (_data, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', courseId] });
      queryClient.invalidateQueries({ queryKey: ['gamification', courseId] });
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// PRIZES
// ═══════════════════════════════════════════════════════════════════════════

export const fetchPrizes = async (
  params: PrizesQueryParams = {}
): Promise<{ prizes: Prize[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.category) queryParams.append('category', params.category);
  if (params.is_draft !== undefined) queryParams.append('is_draft', String(params.is_draft));

  const response = await api.get<PrizesListResponse>(`/prizes?${queryParams.toString()}`);
  return {
    prizes: response.data.data.prizes ?? [],
    pagination: response.data.data.pagination,
  };
};

export const usePrizes = (
  params: PrizesQueryParams = {}
): UseQueryResult<{ prizes: Prize[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['prizes', params],
    queryFn: () => fetchPrizes(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });
};

export const fetchPrize = async (prizeId: string): Promise<Prize> => {
  const response = await api.get<PrizeResponse>(`/prizes/${prizeId}`);
  return response.data.data;
};

export const usePrize = (
  prizeId: string | undefined
): UseQueryResult<Prize, Error> => {
  return useQuery({
    queryKey: ['prizes', prizeId],
    queryFn: () => fetchPrize(prizeId!),
    enabled: !!prizeId,
    staleTime: 2 * 60 * 1000,
  });
};

export const createPrize = async (data: PrizeCreate): Promise<Prize> => {
  const response = await api.post<PrizeResponse>('/prizes', data);
  return response.data.data;
};

export const useCreatePrize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPrize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
    },
  });
};

export const updatePrize = async (prizeId: string, data: PrizeUpdate): Promise<Prize> => {
  const response = await api.put<PrizeResponse>(`/prizes/${prizeId}`, data);
  return response.data.data;
};

export const useUpdatePrize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ prizeId, data }: { prizeId: string; data: PrizeUpdate }) =>
      updatePrize(prizeId, data),
    onSuccess: (_data, { prizeId }) => {
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
      queryClient.invalidateQueries({ queryKey: ['prizes', prizeId] });
    },
  });
};

export const deletePrize = async (prizeId: string): Promise<void> => {
  await api.delete(`/prizes/${prizeId}`);
};

export const useDeletePrize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePrize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
    },
  });
};

export const publishPrize = async (prizeId: string): Promise<void> => {
  await api.post(`/prizes/${prizeId}/publish`);
};

export const usePublishPrize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishPrize,
    onSuccess: (_data, prizeId) => {
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
      queryClient.invalidateQueries({ queryKey: ['prizes', prizeId] });
    },
  });
};

export const unpublishPrize = async (prizeId: string): Promise<void> => {
  await api.post(`/prizes/${prizeId}/unpublish`);
};

export const useUnpublishPrize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unpublishPrize,
    onSuccess: (_data, prizeId) => {
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
      queryClient.invalidateQueries({ queryKey: ['prizes', prizeId] });
    },
  });
};

export const claimPrize = async (
  prizeId: string,
  data: PrizeClaimCreate
): Promise<PrizeClaim> => {
  const response = await api.post<ClaimResponse>(`/prizes/${prizeId}/claim`, data);
  return response.data.data;
};

export const useClaimPrize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ prizeId, data }: { prizeId: string; data: PrizeClaimCreate }) =>
      claimPrize(prizeId, data),
    onSuccess: (_data, { prizeId }) => {
      queryClient.invalidateQueries({ queryKey: ['prizes', prizeId] });
      queryClient.invalidateQueries({ queryKey: ['prize-claims'] });
      queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// PRIZE CLAIMS
// ═══════════════════════════════════════════════════════════════════════════

export const fetchClaims = async (
  params: ClaimsQueryParams = {}
): Promise<{ claims: PrizeClaim[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.learner_id) queryParams.append('learner_id', params.learner_id);
  if (params.prize_id) queryParams.append('prize_id', params.prize_id);
  if (params.status) queryParams.append('status', params.status);

  const response = await api.get<ClaimsListResponse>(`/prize-claims?${queryParams.toString()}`);
  return {
    claims: response.data.data.claims ?? [],
    pagination: response.data.data.pagination,
  };
};

export const useClaims = (
  params: ClaimsQueryParams = {}
): UseQueryResult<{ claims: PrizeClaim[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['prize-claims', params],
    queryFn: () => fetchClaims(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });
};

export const fetchClaim = async (claimId: string): Promise<PrizeClaim> => {
  const response = await api.get<ClaimResponse>(`/prize-claims/${claimId}`);
  return response.data.data;
};

export const useClaim = (
  claimId: string | undefined
): UseQueryResult<PrizeClaim, Error> => {
  return useQuery({
    queryKey: ['prize-claims', claimId],
    queryFn: () => fetchClaim(claimId!),
    enabled: !!claimId,
    staleTime: 2 * 60 * 1000,
  });
};

export const fulfillClaim = async (claimId: string, data?: PrizeClaimAction): Promise<PrizeClaim> => {
  const response = await api.post<ClaimResponse>(`/prize-claims/${claimId}/fulfill`, data);
  return response.data.data;
};

export const useFulfillClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, data }: { claimId: string; data?: PrizeClaimAction }) =>
      fulfillClaim(claimId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prize-claims'] });
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
    },
  });
};

export const cancelClaim = async (claimId: string, data?: PrizeClaimAction): Promise<PrizeClaim> => {
  const response = await api.post<ClaimResponse>(`/prize-claims/${claimId}/cancel`, data);
  return response.data.data;
};

export const useCancelClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, data }: { claimId: string; data?: PrizeClaimAction }) =>
      cancelClaim(claimId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prize-claims'] });
      queryClient.invalidateQueries({ queryKey: ['prizes'] });
      queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// POINTS LEDGER
// ═══════════════════════════════════════════════════════════════════════════

export const fetchPointsBalance = async (learnerId: string): Promise<PointsBalance> => {
  const response = await api.get<PointsBalanceResponse>(`/learners/${learnerId}/points`);
  return response.data.data;
};

export const usePointsBalance = (
  learnerId: string | undefined
): UseQueryResult<PointsBalance, Error> => {
  return useQuery({
    queryKey: ['points', learnerId, 'balance'],
    queryFn: () => fetchPointsBalance(learnerId!),
    enabled: !!learnerId,
    staleTime: 60 * 1000,
  });
};

export const fetchLearnerLedger = async (
  learnerId: string,
  params: LedgerQueryParams = {}
): Promise<{ entries: LedgerEntry[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.reason) queryParams.append('reason', params.reason);

  const response = await api.get<LedgerListResponse>(
    `/learners/${learnerId}/points/ledger?${queryParams.toString()}`
  );
  return {
    entries: response.data.data.entries ?? [],
    pagination: response.data.data.pagination,
  };
};

export const useLearnerLedger = (
  learnerId: string | undefined,
  params: LedgerQueryParams = {}
): UseQueryResult<{ entries: LedgerEntry[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['points', learnerId, 'ledger', params],
    queryFn: () => fetchLearnerLedger(learnerId!, params),
    enabled: !!learnerId,
    staleTime: 60 * 1000,
  });
};

export const adjustPoints = async (learnerId: string, data: AdminAdjust): Promise<void> => {
  await api.post(`/learners/${learnerId}/points/adjust`, data);
};

export const useAdjustPoints = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ learnerId, data }: { learnerId: string; data: AdminAdjust }) =>
      adjustPoints(learnerId, data),
    onSuccess: (_data, { learnerId }) => {
      queryClient.invalidateQueries({ queryKey: ['points', learnerId] });
      queryClient.invalidateQueries({ queryKey: ['points', 'tenant'] });
    },
  });
};

export const fetchTenantLedger = async (
  params: TenantLedgerQueryParams = {}
): Promise<{ entries: LedgerEntry[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.learner_id) queryParams.append('learner_id', params.learner_id);
  if (params.reason) queryParams.append('reason', params.reason);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);

  const response = await api.get<LedgerListResponse>(
    `/points/ledger?${queryParams.toString()}`
  );
  return {
    entries: response.data.data.entries ?? [],
    pagination: response.data.data.pagination,
  };
};

export const useTenantLedger = (
  params: TenantLedgerQueryParams = {}
): UseQueryResult<{ entries: LedgerEntry[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['points', 'tenant', params],
    queryFn: () => fetchTenantLedger(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// LEADERBOARD (admin live current-season standings)
// ═══════════════════════════════════════════════════════════════════════════

export const fetchAdminLeaderboard = async (
  params: LeaderboardQueryParams = {}
): Promise<{ rankings: LeaderboardRanking[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());

  const qs = queryParams.toString();
  const response = await api.get<AdminLeaderboardResponse>(
    `/gamification/leaderboard${qs ? `?${qs}` : ''}`
  );
  return {
    rankings: response.data.data ?? [],
    pagination: response.data.meta,
  };
};

export const useAdminLeaderboard = (
  params: LeaderboardQueryParams = {}
): UseQueryResult<{ rankings: LeaderboardRanking[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['gamification', 'leaderboard', params],
    queryFn: () => fetchAdminLeaderboard(params),
    staleTime: 60 * 1000,
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// LEADERBOARD RESET SCHEDULE
// ═══════════════════════════════════════════════════════════════════════════

export const fetchResetSchedule = async (): Promise<ResetSchedule> => {
  const response = await api.get<ResetScheduleResponse>('/gamification/schedule');
  return response.data.data;
};

export const useResetSchedule = (): UseQueryResult<ResetSchedule, Error> => {
  return useQuery({
    queryKey: ['gamification', 'schedule'],
    queryFn: fetchResetSchedule,
    staleTime: 2 * 60 * 1000,
  });
};

export const updateResetSchedule = async (
  data: ResetScheduleUpdate
): Promise<ResetSchedule> => {
  const response = await api.put<ResetScheduleResponse>('/gamification/schedule', data);
  return response.data.data;
};

export const useUpdateResetSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateResetSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification', 'schedule'] });
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// SEASONS (history) + PREVIOUS-MONTHS REPORT
// ═══════════════════════════════════════════════════════════════════════════

/** All seasons for the tenant (current + past), newest first. */
export const fetchSeasons = async (): Promise<GamificationSeason[]> => {
  const response = await api.get<SeasonsResponse>('/gamification/seasons', {
    params: { per_page: 100 },
  });
  return response.data.data;
};

export const useSeasons = (): UseQueryResult<GamificationSeason[], Error> => {
  return useQuery({
    queryKey: ['gamification', 'seasons'],
    queryFn: fetchSeasons,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Close the running season and open a fresh one: standings are archived and
 * every balance returns to zero. The ledger is immutable, so the reset is
 * recorded as a `season_reset` entry per learner — nothing is erased, the
 * history simply moves into the season report.
 *
 * The endpoint has existed since seasons shipped; nothing in the UI called it
 * until now (Aleix, 2026-09-09: two-month-old scores with no way to clear
 * them).
 */
export const resetLeaderboard = async (label?: string): Promise<{ season: GamificationSeason }> => {
  const response = await api.post<{ data: { season: GamificationSeason } }>(
    '/gamification/leaderboard/reset',
    label ? { label } : {}
  );
  return response.data.data;
};

export const useResetLeaderboard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetLeaderboard,
    onSuccess: () => {
      // Balances, standings and the season list all move at once.
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
};

/**
 * Download the CSV report for the closed season that started in the given
 * year/month. Returns the raw CSV blob; the caller triggers the browser save.
 */
export const downloadMonthReport = async (
  year: number,
  month: number
): Promise<Blob> => {
  const response = await api.get('/gamification/report', {
    params: { year, month },
    responseType: 'blob',
  });
  return response.data as Blob;
};

/** Live report for the season in progress — who has what, and which
 *  prizes they claimed (with dates), before the reset archives it. */
export const downloadCurrentSeasonReport = async (): Promise<Blob> => {
  const response = await api.get('/gamification/report', {
    params: { current: true },
    responseType: 'blob',
  });
  return response.data as Blob;
};
